"use client";

import React, { useState, useEffect } from "react";
import BookingTable from "../common/Table";
import {
  getVehicleTypeList,
  useUpdateVehicleTypeStatus,
  useAddVehicleType,
  useDeleteVehicleStatusMutation,
  useUpdateVehicleTypeOrder,
  useUpdateVehicleTypeDetails,
  useGetCompanyDetail // Already imported
} from "@/hooks/useCommon";
import {
  X,
  Trash2,
  Edit,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Plus,
  Search,
} from "lucide-react";
import DynamicForm from "./Popup";
import Button from "./Button";  
import { toast } from "react-toastify";
import { useAuth } from "@/store/auth";


interface TableData {
  sno: number;
  order: number;
  image: string;
  vehicleType: string;
  vehicle_type?: string;
  vehicle_image?: string;
  seating_capacity: number;
  luggage: number;
  createdBy: string;
  status: string;
  vehicle_type_name: string;
}

interface CompanyData {
  companyData: {
    id: number;
    company_name: string;
    brand_name: string | null;
    // ... other company fields
  };
  gstData: any[];
}

const ManageVehicleType = () => {
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleImage, setVehicleImage] = useState("");
  const [companyId, setCompanyId] = useState<string>(""); // Add company state
  const [activeStatus, setActiveStatus] = useState(true);
  const [inactiveStatus, setInactiveStatus] = useState(false);
  const [currentRow, setCurrentRow] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { data: vehicleDetail, refetch } = getVehicleTypeList();
  const [rawVehicleData, setRawVehicleData] = useState<any[]>([]);
  const [filteredVehicleData, setFilteredVehicleData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const { mutate: updateStatusMutate } = useUpdateVehicleTypeStatus();
  const { mutate: addVehicleTypeMutate } = useAddVehicleType();
  const { mutate: deleteVehicleTypeMutate } = useDeleteVehicleStatusMutation();
  const { mutate: updateVehicleTypeOrder } = useUpdateVehicleTypeOrder();
  const { mutate: updateVehicleTypeDetails } = useUpdateVehicleTypeDetails();

  // Add company data hook
  const { data: companyData, isLoading: companyLoading } = useGetCompanyDetail();
  
  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
  const user = useAuth();
  const userId = user?.user?.id;

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (Array.isArray(vehicleDetail)) {
      setRawVehicleData(vehicleDetail);
      setFilteredVehicleData(vehicleDetail);
    }
  }, [vehicleDetail]);

  // Format company options from API response
  const companyOptions = React.useMemo(() => {
    if (!companyData || !Array.isArray(companyData)) {
      return [];
    }
    
    return companyData.map((item: CompanyData) => ({
      value: item.companyData.id.toString(),
      label: item.companyData.company_name || `Company ${item.companyData.id}`
    }));
  }, [companyData]);

  // Handle drag and drop reordering
  const handleReorder = async (reorderedData: any[]) => {
    try {
      const ids: number[] = [];
      const displayOrders: number[] = [];

      reorderedData.forEach((item, index) => {
        ids.push(item.id);
        displayOrders.push(index + 1);
      });

      updateVehicleTypeOrder(
        {
          id: ids,
          display_order: displayOrders,
        },
        {
          onSuccess: (data) => {
            toast.success("Display order updated successfully");
            refetch();
          },
          onError: (error) => {
            toast.error("Failed to update display order");
            console.error("Error updating display order:", error);
            refetch();
          },
        }
      );
    } catch (error) {
      console.error("Error in handleReorder:", error);
      toast.error("Failed to update display order");
      refetch();
    }
  };

  const vechicleData = Array.isArray(filteredVehicleData)
    ? filteredVehicleData.map((item: any, index: number) => ({
        sno: index + 1,
        display_order: item.display_order,
        amenities: item.amenities,
        id: item.id,
        company_id: item.company_id,
        user_id: item.user_id,
        vehicle_image: item.vehicle_image,
        vehicle_type: item.vehicle_type,
        seating_capacity: item.seating_capacity,
        luggage: item.luggage,
        status: item.status,
        created_name: item.created_name,
        action: "View",
      }))
    : [];

  const handleSearch = () => {
    const filtered = rawVehicleData.filter((item) => {
      const matchVehicleType = vehicleType ? item.id == vehicleType : true;
      const matchCompany = companyId ? item.company_id == companyId : true; // Add company filter

      const matchStatus =
        activeStatus && inactiveStatus
          ? true
          : activeStatus
          ? item.status == 1
          : inactiveStatus
          ? item.status == 0
          : true;

      return matchVehicleType && matchCompany && matchStatus;
    });

    setFilteredVehicleData(filtered);
  };

  // Handle add form submit with company_id and user_id
  const handleAddFormSubmit = (formData: Record<string, any>) => {
    const { vehicleType, status, vehicleImage, vehicle_type, seating_capacity, luggage, display_order, company_id } = formData;

    const file = vehicleImage instanceof FileList ? vehicleImage[0] : vehicleImage;

    addVehicleTypeMutate(
      {
        file,
        folder: "vehicle-type-image",
        category_id: "0",
        status,
        vehicle_type,
        seating_capacity: seating_capacity ? parseInt(seating_capacity) : undefined,
        luggage: luggage ? parseInt(luggage) : undefined,
        display_order: display_order ? parseInt(display_order) : undefined,
        company_id: company_id ? parseInt(company_id) : undefined,
        user_id: userId,
      },
      {
        onSuccess: (res) => {
          toast.success("Vehicle type added successfully");
          refetch();
          setIsAddModalOpen(false);
          setCurrentRow(null);
        },
        onError: () => {
          toast.error("Failed to add vehicle type");
        },
      }
    );
  };

  // Handle update form submit with company_id and user_id
  const handleUpdateFormSubmit = (formData: Record<string, any>) => {
    const { vehicle_type, status, vehicleImage, seating_capacity, luggage, display_order, company_id } = formData;

    const file = vehicleImage instanceof FileList ? vehicleImage[0] : vehicleImage;

    updateVehicleTypeDetails(
      {
        id: currentRow.id,
        vehicle_type,
        status,
        seating_capacity: seating_capacity ? parseInt(seating_capacity) : undefined,
        luggage: luggage ? parseInt(luggage) : undefined,
        display_order: display_order ? parseInt(display_order) : undefined,
        company_id: company_id ? parseInt(company_id) : undefined,
        user_id: userId,
        file: file,
      },
      {
        onSuccess: (data) => {
          if (data?.status === "success") {
            toast.success("Vehicle type updated successfully");
            refetch();
            setIsModalOpen(false);
            setCurrentRow(null);
          } else {
            toast.error(data?.message || "Failed to update vehicle type");
          }
        },
        onError: (error) => {
          toast.error("Failed to update vehicle type");
          console.error("Update error:", error);
        },
      }
    );
  };

  const onStatusChanged = (status_id: number, row_id?: any) => {
    let type_ids = [row_id];
    if (!row_id) {
      if (selectedRowIds.length === 0 && row_id === undefined) {
        toast.error("No rows selected");
        return;
      }

      const selectedRoutes = filteredVehicleData.filter((row) =>
        selectedRowIds.includes(row.id)
      );
      type_ids = selectedRoutes.map((row) => row.id);
    }
    updateStatusMutate(
      {
        status: status_id,
        id: type_ids,
      },
      {
        onSuccess: (data) => {
          if (data.status == "success") {
            toast.success(
              status_id == 2 ? "Data deleted successully" : data?.message
            );
          }
          refetch();
          setSelectedRowIds([]);
        },
        onError: (error) => {
          toast.error("Something want wrong");
        },
      }
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = filteredVehicleData.map((row) => row.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedRowIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const allRowsSelected =
    filteredVehicleData?.length > 0 &&
    selectedRowIds?.length === filteredVehicleData?.length;

  const getImageUrl = (url: any) => {
    const rawFilePath = url || "";
    const fileParts = rawFilePath.split("/");
    const folderPath = fileParts.slice(0, -1).join("/");
    const fileName = fileParts[fileParts.length - 1];
    const imageUrl = `${profilePath}${folderPath}/${encodeURIComponent(
      fileName
    )}`;
    return imageUrl;
  };

  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={allRowsSelected}
        />
      ),
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRowIds.includes(row.id)}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    {
      key: "display_order",
      header: "Order",
      sortable: true,
      render: (value: any, row: any) => (
        <div className="flex items-center gap-2">
          <span className="bg-gray-100 px-2 py-1 rounded text-xs font-medium">
            {value}
          </span>
        </div>
      ),
    },
    {
      key: "vehicle_image",
      header: "Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.vehicle_image);
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-full h-48 object-contain rounded bg-white"
          />
        );
      },
    },
    { key: "vehicle_type", header: "Vehicle Type", sortable: true },
    { key: "id", header: "id", sortable: true, hidden: true },
    { key: "created_name", header: "Created By", sortable: true },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: number | string) => {
        const isOnline = Number(value) == 1;
        return (
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                isOnline ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm font-medium">
              {isOnline ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => onStatusChanged(2, row.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  const vehicleOptions = Array.isArray(vehicleDetail)
    ? vehicleDetail.map((item: any) => ({
        label: item.vehicle_type,
        value: item.id,
      }))
    : [];

  // Updated form fields with company dropdown
  const formFields: any = [
    {
      id: "company_id",
      label: "Company",
      type: "select",
      placeholder: "Select Company",
      required: true,
      defaultValue: currentRow?.company_id?.toString() || "",
      options: companyOptions,
    },
    {
      id: "vehicle_type",
      label: "Vehicle Type",
      type: "text",
      placeholder: "Enter Vehicle Type",
      required: true,
      defaultValue: currentRow?.vehicle_type || "",
    },
    {
      id: "seating_capacity",
      label: "Seating Capacity",
      type: "number",
      placeholder: "Enter seating capacity",
      defaultValue: currentRow?.seating_capacity || ""
    },
    {
      id: "luggage",
      label: "Luggage Capacity",
      type: "number",
      placeholder: "Enter luggage capacity",
      defaultValue: currentRow?.luggage || ""
    },
    {
      id: "display_order",
      label: "Order by",
      type: "number",
      placeholder: "Display order",
      defaultValue: currentRow?.display_order || ""
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "Select Status",
      required: true,
      defaultValue: currentRow?.status?.toString() || "1",
      options: [
        { value: "1", label: "Active" },
        { value: "0", label: "In-Active" },
      ],
    },
    {
      id: "vehicleImage",
      label: "Vehicle Image",
      type: "file",
      placeholder: "Upload Vehicle Image",
      required: currentRow ? false : true,
      defaultValue: currentRow?.vehicle_image 
        ? getImageUrl(currentRow.vehicle_image)
        : "",
    },
  ];

  const handleClear = () => {
    setVehicleType("");
    setCompanyId(""); // Clear company filter
    setActiveStatus(true);
    setInactiveStatus(false);
    setFilteredVehicleData(rawVehicleData);
  };

  return (
    <div className="w-full bg-white">
      <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        {/* Vehicle Type */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Vehicle Type
          </label>
          <select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            <option value="">Select Vehicle Type</option>
            {vehicleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Company Dropdown */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Company
          </label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            disabled={companyLoading}
          >
            <option value="">All Companies</option>
            {companyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex items-center gap-4">
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="active"
                  checked={activeStatus}
                  onChange={(e) => setActiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setActiveStatus(!activeStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    activeStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      activeStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="active"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* In-Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="inactive"
                  checked={inactiveStatus}
                  onChange={(e) => setInactiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setInactiveStatus(!inactiveStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    inactiveStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      inactiveStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="inactive"
                className="text-sm text-gray-700 cursor-pointer"
              >
                In-Active
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleSearch}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search
          </Button>

          <Button
            size="sm"
            onClick={handleClear}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Vehicle Type
          </Button>

          <Button
            size="sm"
            onClick={() => onStatusChanged(1)}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Publish
          </Button>

          <Button
            size="sm"
            onClick={() => onStatusChanged(0)}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Unpublish
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={() => onStatusChanged(2)}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Delete
          </Button>
        </div>

        {/* Add drag and drop configuration */}
        <BookingTable
          data={vechicleData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          itemsPerPage={10}
          dragDrop={{
            enabled: true,
            onReorder: handleReorder,
          }}
        />
      </div>

      {/* Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentRow(null);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Update Vehicle Type
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentRow(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={handleUpdateFormSubmit}
                  submitText="Update Vehicle Type"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsAddModalOpen(false);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add New Vehicle Type
                </h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields.map((field: any) => ({
                    ...field,
                    defaultValue: field.id === "status" ? "1" : "",
                  }))}
                  onSubmit={handleAddFormSubmit}
                  submitText="Add Vehicle Type"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageVehicleType;





